import { Injectable } from '@angular/core';
import { SalesforceDataSourceApiService, ISalesforceObjectMapping, ISalesforceRelatedObject } from 'datasource-service-lib';
import { IDocumentDestinationService, IEntity, } from 'interfaces-lib';
import { DocumentDestinationActionProperties, TemplateWorkflowConfig } from 'interfaces-lib';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class SalesforceDocumentDestinationService implements IDocumentDestinationService {
  constructor(private datasourceService: SalesforceDataSourceApiService) {}

  getDocumentDestinations(
    entity: IEntity,
    template: TemplateWorkflowConfig,
    dialogResult?: IEntity[]
  ): Observable<IEntity[]> {
    const entityTypes: string[] | undefined = this.getDestinationEntityTypesFromTemplateConfig(entity.type, template);
    const possibleDestinations: IEntity[] = [entity, ...(dialogResult?.length ? dialogResult : [])];
    if (!entityTypes) {
      return of(possibleDestinations);
    }

    return this.resolveRelatedDestinations(entity, entityTypes).pipe(
      map((relatedDestinations) =>
        this.filterOnConfigDestinations(
          this.dedupeDestinations([...possibleDestinations, ...relatedDestinations]),
          entityTypes
        )
      )
    );
  }

  private filterOnConfigDestinations(destinations: IEntity[], entityTypes: string[]) {
    return destinations.filter((x) => entityTypes.map((y) => y.toLowerCase()).includes(x.type.toLowerCase()));
  }

  private getDestinationEntityTypesFromTemplateConfig(entityType: string, template: TemplateWorkflowConfig): string[] | undefined {
    const actions = template.actionConfigs.filter((x) => x.type === 'documentDestination');
    if (!actions || !actions.length) {
      return undefined;
    }

    const properties = actions.find((x) => (x.properties as DocumentDestinationActionProperties).entityType === entityType)
      ?.properties as DocumentDestinationActionProperties;
    if (!properties || !properties.paths) {
      return [];
    }

    return properties.paths.map((x) => this.pathDisplay(x));
  }

  private pathDisplay(path: string) {
    return path.split('.').slice(-1).toString();
  }

  private resolveRelatedDestinations(entity: IEntity, entityTypes: string[]): Observable<IEntity[]> {
    const targetEntityTypes = entityTypes.filter((type) => type.toLowerCase() !== entity.type.toLowerCase());
    if (!targetEntityTypes.length) {
      return of([]);
    }

    return this.datasourceService.getEntityMappings().pipe(
      take(1),
      switchMap((mappings) => {
        const mapping = this.findMapping(mappings, entity.type);
        const relatedObjects = this.matchRelatedObjects(mapping, targetEntityTypes);
        if (!relatedObjects.length) {
          return of([]);
        }

        const fields = this.buildRelationshipFields(relatedObjects);
        return this.datasourceService.get(entity, { fields }).pipe(
          map((record) => this.mapRelatedRecordEntities(record, relatedObjects)),
          catchError(() => of([]))
        );
      })
    );
  }

  private findMapping(mappings: ISalesforceObjectMapping[] | null | undefined, objectName: string): ISalesforceObjectMapping | undefined {
    return (mappings ?? []).find((mapping) => mapping.objectName?.toLowerCase() === objectName?.toLowerCase());
  }

  private matchRelatedObjects(
    mapping: ISalesforceObjectMapping | undefined,
    entityTypes: string[]
  ): ISalesforceRelatedObject[] {
    const requested = entityTypes.map((type) => type.toLowerCase());
    return (mapping?.relatedObjects ?? []).filter((related) => requested.includes(related.objectName.toLowerCase()));
  }

  private buildRelationshipFields(relatedObjects: ISalesforceRelatedObject[]): string {
    return relatedObjects
      .flatMap((related) => [`${related.relationSegment}.Id`, `${related.relationSegment}.Name`])
      .join(',');
  }

  private mapRelatedRecordEntities(record: any, relatedObjects: ISalesforceRelatedObject[]): IEntity[] {
    return relatedObjects
      .map((related) => {
        const relatedRecord = record?.[related.relationSegment];
        if (!relatedRecord?.Id) {
          return undefined;
        }
        return {
          id: relatedRecord.Id,
          type: related.objectName,
          name: relatedRecord.Name ?? relatedRecord.name ?? relatedRecord.Id,
        } as IEntity;
      })
      .filter((entity): entity is IEntity => Boolean(entity));
  }

  private dedupeDestinations(destinations: IEntity[]): IEntity[] {
    const seen = new Set<string>();
    return destinations.filter((destination) => {
      const key = `${destination.type.toLowerCase()}:${destination.id}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }
}
